import i18n, { type ParseKeys } from 'i18next';
import { initReactI18next } from 'react-i18next';
import { CATALOG as TRIP_DOC_CATALOG } from '@voll-intelligence/trip-document';
import {
  CATALOG as TRIP_PANEL_CATALOG,
  configureTripPanelI18n,
} from '@voll-intelligence/trip-panel';

import { useUiLocalePreference } from './locale_store';

import enCommon from './en/common.json';
import { resolveUiLocale, type UiLocalePreference } from './locale';
import ptBrCommon from './pt-BR/common.json';

const defaultNS = 'common';
const fallbackLng = 'pt-BR';

// O itinerário da viagem (`@voll-intelligence/trip-panel`) traduz pelo NOSSO i18next:
// os catálogos dele e do trip-document entram no namespace comum (mesmo arranjo do
// web-chat). `common.json` vem por último e vence em qualquer colisão.
const resources = {
  'pt-BR': {
    common: { ...TRIP_DOC_CATALOG['pt-BR'], ...TRIP_PANEL_CATALOG['pt-BR'], ...ptBrCommon },
  },
  en: { common: { ...TRIP_DOC_CATALOG.en, ...TRIP_PANEL_CATALOG.en, ...enCommon } },
} as const;

if (!i18n.isInitialized) {
  void i18n.use(initReactI18next).init({
    resources,
    // O idioma da INTERFACE vem da preferência local do operador (`auto` segue o
    // navegador). Não confundir com o idioma do cliente na conversa.
    lng: resolveUiLocale(useUiLocalePreference.getState().preference, navigator.language),
    fallbackLng,
    defaultNS,
    ns: [defaultNS],
    // Locale JSONs use flat dotted keys (e.g. "auth.brand.os"); disable
    // separators so i18next treats them literally instead of nesting.
    keySeparator: false,
    nsSeparator: false,
    returnNull: false,
    interpolation: { escapeValue: false },
    react: { useSuspense: false },
  });
}

// `<html lang>` acompanha o idioma ativo — leitores de tela e o corretor do
// browser leem daqui. Roda no boot e em toda troca (padrão do web-chat).
function syncDocumentLang(lng: string): void {
  if (typeof document !== 'undefined') document.documentElement.lang = lng;
}
syncDocumentLang(i18n.language || fallbackLng);
i18n.on('languageChanged', syncDocumentLang);

export function t(key: ParseKeys, options?: Record<string, unknown>): string {
  return i18n.t(key, options);
}

/** Current active UI locale (BCP-47), e.g. 'pt-BR' or 'en'. */
export function currentLocale(): string {
  return i18n.language;
}

/**
 * Grava a preferência do operador e aplica o idioma resolvido. O app traduz com
 * `t()` imperativo, então quem precisa re-renderizar assina `onLocaleChanged`
 * (o `LocaleRoot` remonta a árvore inteira nesse sinal).
 */
export async function setUiLocalePreference(pref: UiLocalePreference): Promise<void> {
  useUiLocalePreference.getState().setPreference(pref);
  const next = resolveUiLocale(pref, navigator.language);
  if (next === i18n.language) return;
  await i18n.changeLanguage(next);
}

/** Assinatura de troca de idioma; devolve o unsubscribe. */
export function onLocaleChanged(listener: () => void): () => void {
  i18n.on('languageChanged', listener);
  return () => {
    i18n.off('languageChanged', listener);
  };
}

// O `TranslateFn` do pacote é frouxo por design (`key: string` — não conhece o
// `ParseKeys` do host); as chaves reais vêm dos catálogos já fundidos em `resources`.
configureTripPanelI18n({
  // eslint-disable-next-line @typescript-eslint/no-unnecessary-type-assertion -- tsc exige a asserção aqui (mais largo que o programa do eslint); ver tsc --noEmit
  t: (key, vars) => i18n.t(key as ParseKeys, vars),
  currentLocale,
});
